// LICENSE_CODE ZON
'use strict'; /*jslint ts:true, react: true*/
import React, {useState, Fragment, useEffect, useMemo} from 'react';
// @ts-ignore
import {Tooltip} from './tooltip';
import {Input} from './input';
import {Button} from '../../../shared/button';
// @ts-ignore
import {parse, strftime} from '/util/date.js';
import cn from 'classnames';
import styles from './datetime.module.less';
import {Month} from './datetime/month';
import {Time_input} from './datetime/timeinput';
import {Month_selector} from './datetime/month_selector';
import Icon_calendar from '../img/svg/icon/calendar.svg';

interface i_datetime_props {
    value: string;
    on_change: (value:string)=>void;
    name?: string;
    placeholder?: string;
    cls?: string;
    no_margin?: boolean;
    label?: string|JSX.Element;
    format_date?: string;
    use_utc?: boolean;
    to_right?: boolean;
    sidebar?: boolean;
    time?: boolean;
    with_cta?: boolean;
    is_error?: boolean;
    can_focus?: boolean;
    on_focus?: ()=>void;
    min_date?: Date;
    fixed_on_mobile?: boolean;
    dateonly?: boolean;
    icon?: JSX.Element;
    min_year?: number;
    max_year?: number;
}

interface i_years_props {
    pick: (number)=>void;
    active: number;
    min?: number;
    max?: number;
}
const Years = (props: i_years_props)=>{
    const cur_year = new Date().getFullYear();
    const {pick, active, min = cur_year - 100, max = cur_year - 1} = props;
    const years = Array.from({length: max - min + 1}, (_, i)=>i+min)
        .sort((a, b)=>b - a);
    return <div className={styles.years}>
      {years.map(y=><span key={y} onClick={()=>pick(y)}
        className={cn(styles.year_cell, y==active && styles.active)}>
        {y}
      </span>)}
    </div>;
};

interface i_months_props {
    pick: (number)=>void;
    active: number;
}
const Months = (props: i_months_props)=>{
    const {pick, active} = props;
    const months = useMemo(()=>{
        const d = new Date();
        const res = [];
        for (let i = 0; i < 12; i++)
        {
            d.setMonth(i);
            res.push(d.toLocaleString('default', {month: 'short'}));
        }
        return res;
    }, []);
    return <div className={styles.months}>
      {months.map((m, i)=><span key={m} onClick={()=>pick(i)}
        className={cn(i==active && styles.active)}>
        {m}
      </span>)}
    </div>;
};

const Date_picker = ({date, on_change, view_month, view_year,
    min_year, max_year, on_change_view_month, min_date, dateonly=false})=>{
    const [mode, set_mode] = useState<'dates'|'months'|'years'>('dates');
    const on_select = ()=>{
        if (mode=='dates')
            set_mode('years');
        else
            set_mode('dates');
    };
    const on_pick_year = v=>{
        on_change_view_month(undefined, v);
        set_mode('months');
    };
    const on_pick_month = v=>{
        on_change_view_month(v);
        set_mode('dates');
    };
    const cls = cn(styles.date_picker, dateonly && styles.dateonly);
    return <div className={cls} data-elem="date_picker">
      <Month_selector
        on_change={(new_month, new_year)=>{
          on_change_view_month(new_month, new_year);
        }} view_month={view_month} view_year={view_year} dateonly
        hide_next={max_year!=null && view_month >= 11 && view_year >= max_year}
        hide_prev={min_year!=null && view_month <= 0 && view_year <= min_year}
        on_select={on_select}
      />
      {mode=='dates' && <Month dateonly={dateonly} value={date}
        on_change={new_date=>{
          on_change(new_date);
        }} min_date={min_date} view_month={view_month} view_year={view_year}
      />}
      {mode=='years' && <Years
        pick={on_pick_year} active={view_year}
        min={min_year} max={max_year}
      />}
      {mode=='months' && <Months
        pick={on_pick_month}
        active={view_month}
      />}
    </div>;
};

const Sidebar = ({items, on_change})=>{
    const set_preset = diff=>{
        if (diff)
        {
            const now = new Date();
            now.setDate(now.getDate() + (diff.d || 0));
            now.setHours(now.getHours() + (diff.h || 0));
            on_change(now);
        }
    };
    const [active, set_active] = useState(items.find(it=>it.active).text);
    return <Fragment>
      {items.map((item, index)=><div key={index}
        className={cn(styles.item, item.text == active && styles.active)}
        onClick={()=>{
          set_active(item.text);
          set_preset(item.diff);
        }}>
        {item.text}
      </div>)}</Fragment>;
};

export const Datetime = ({value, on_change, name, placeholder, cls, no_margin,
    label, format_date, use_utc, to_right, sidebar=true, time=true,
    with_cta=true, is_error, can_focus, on_focus, min_date, fixed_on_mobile,
    dateonly, icon, min_year, max_year}
    :i_datetime_props)=>{
    const format_date_res = dateonly ? '%Y-%m-%d' :
        format_date || '%Y-%m-%d %H:%M:%S';
    const [string_value, set_string_value] = useState(value);
    const [date_value, set_date_value] = useState(value ?
      parse(value, {fmt: format_date_res, local: !use_utc}) : null);
    const date_for_view = date_value || parse('today');
    const input_value = dateonly && !!date_value ?
        date_for_view.toLocaleDateString() : string_value;
    const [view_month, set_view_month] = useState(date_for_view.getMonth());
    const [view_year, set_view_year] = useState(date_for_view.getFullYear());
    const tooltip_ref = {close: ()=>{
      // XXX dmitriiz: change interface and remove this fn
      console.log('close');
    }};
    const shortcuts = [
        {text: 'Custom', default: true, active: true},
        {text: 'Hour ago', diff: {h: -1}},
        {text: '4 hours ago', diff: {h: -4}},
        {text: '24 hours ago', diff: {h: -24}},
        {text: 'Now', diff: {}},
        {text: 'Yesterday', diff: {d: -1}},
        {text: '7 days ago', diff: {d: -7}},
    ];
    const handle_change = new_value=>{
        set_string_value(new_value);
        tooltip_ref.close();
        const date=parse(new_value, {fmt: format_date_res});
        if (value!=new_value &&
            (format_date || new_value.length==19) &&
            date)
        {
            on_change(strftime(format_date_res, date, {
              local: !use_utc,
            }));
        }
    };
    const handle_submit = val=>{
        if (val)
        {
            on_change(strftime(format_date_res, val, {
              local: !use_utc,
            }));
        }
        else
            on_change('');
        tooltip_ref.close();
    };
    const on_change_view_month = (new_month, new_year)=>{
        if (new_month !== undefined)
            set_view_month(new_month);
        if (new_year !== undefined)
            set_view_year(new_year);
    };
    useEffect(()=>{
        if (string_value)
        {
            const date = parse(string_value, {fmt: format_date_res,
                local: !use_utc});
            set_date_value(date ?? parse(strftime(format_date_res,
              new Date(), {
                local: !use_utc,
              }), {fmt: format_date_res, local: !use_utc}));
        }
        else
            set_date_value(null);
    }, [string_value]);
    useEffect(()=>{
        set_string_value(value);
    }, [value]);
    const tt_cls = cn(styles.down, to_right && styles.to_right,
        label && styles.with_label, dateonly && styles.dateonly);
    const input_icon = icon!==undefined ? icon :
        <Icon_calendar width={24} height={24}/>;
    return <Tooltip click_mode only_external_close cls={cn(cls)}
      cls_cn={tt_cls} prop_ref={tooltip_ref}
      fixed_on_mobile={fixed_on_mobile}
      content={<div className={styles.container}>
        {!dateonly && sidebar && <div className={styles.sidebar}>
          <Sidebar items={shortcuts} on_change={date=>{
            set_date_value(date);
          }}/>
        </div>}
        <div className={styles.body}>
          <div>
            <Date_picker date={date_value}
              on_change={date=>{
                set_date_value(date);
                if (!with_cta)
                  handle_submit(date);
              }} key={date_value} view_month={view_month} view_year={view_year}
              on_change_view_month={on_change_view_month} min_date={min_date}
              min_year={min_year} max_year={max_year}
              dateonly={dateonly}
            />
          </div>
          {with_cta && <div className={styles.footer}>
            {!dateonly && time && <div>
              <Time_input date={date_value} on_change={date=>{
                date_value.setHours(date.getHours());
                date_value.setMinutes(date.getMinutes());
                date_value.setSeconds(date.getSeconds());
                set_date_value(date_value);
              }}/>
            </div>}
            <div className={styles.controls}>
              <Button caption="Cancel" size="sm" color="white"
                width={76} on_click={()=>tooltip_ref.close()}/>
              <Button caption="Set Date" size="sm" width={85}
	              on_click={()=>handle_submit(date_value)}/>
            </div>
          </div>}
        </div>
      </div>}>
      <Input name={name} placeholder={placeholder} value={input_value}
        on_change={handle_change} no_margin={no_margin} label={label}
        icon={input_icon} icon_size={'md'}
        left_align_icon error_style={is_error} area_label={'Date selector'}
        on_focus={()=>{
          if (on_focus)
            on_focus();
        }} no_caret={!can_focus}
      />
    </Tooltip>;
};
